// ============================================
// ECO-FRIENDLY DATA CONFIGURATION
// Centralized data for easy updates and maintenance
// ============================================

export const ecoFriendlyData = {
    // Main branding and messaging
    branding: {
      mainTitle: "Environmentally Safe Practices",
      tagline: "Protecting Sun Valley's Environment for 33 Years",
      shortDescription: "We take our responsibility to Sun Valley's watersheds seriously.",
      longDescription: "Our eco-friendly approach protects the children, pets, and wildlife that call this valley home while maintaining beautiful, healthy landscapes.",
      yearsOfService: 33,
      location: "Sun Valley"
    },
  
    // Core eco-friendly practices
    practices: [
      {
        id: "water-conservation",
        title: "Water Conservation",
        shortDescription: "Optimized irrigation maintenance and watering times for healthy, efficient landscapes with minimal water usage.",
        longDescription: "Smart irrigation systems and native plant choices reduce water consumption by up to 40%",
        benefits: [
          "Reduces water bills for property owners",
          "Preserves precious mountain water resources",
          "Creates more drought-resistant landscapes"
        ],
        stats: {
          reductionPercentage: 40,
          metric: "water consumption"
        }
      },
      {
        id: "integrated-pest-management",
        title: "Integrated Pest Management",
        shortDescription: "Using the fewest pesticides possible while maintaining healthy landscapes",
        longDescription: "Our IPM approach reduces chemical applications by 60% compared to traditional methods",
        benefits: [
          "Safer for children and pets",
          "Protects beneficial insects and pollinators",
          "Prevents pesticide resistance development"
        ],
        stats: {
          reductionPercentage: 60,
          metric: "chemical applications"
        }
      },
      {
        id: "watershed-protection",
        title: "Watershed Protection",
        shortDescription: "Kid, trout & pet friendly chemical programs that protect our local waterways",
        longDescription: "All treatments consider Sun Valley's sensitive watershed and aquatic ecosystems",
        benefits: [
          "Protects trout streams and wildlife habitat",
          "Maintains water quality for the community",
          "Supports local ecosystem biodiversity"
        ],
        stats: {
          reductionPercentage: null,
          metric: "watershed-compliant treatments"
        }
      },
      {
        id: "native-plants",
        title: "Native Plant Focus",
        shortDescription: "Emphasizing plants that thrive naturally in our high-altitude mountain environment",
        longDescription: "Native species require 50% less water and virtually no chemical interventions",
        benefits: [
          "Better adapted to local climate conditions",
          "Supports local wildlife and pollinators",
          "Requires minimal maintenance once established"
        ],
        stats: {
          reductionPercentage: 50,
          metric: "water requirements"
        }
      },
      {
        id: "sustainable-materials",
        title: "Sustainable Materials",
        shortDescription: "Using locally-sourced, environmentally responsible materials when possible",
        longDescription: "Recycled materials and local stone reduce transportation impact and support regional economy",
        benefits: [
          "Reduces carbon footprint from transportation",
          "Supports local economy and suppliers",
          "Creates unique, region-appropriate landscapes"
        ],
        stats: {
          reductionPercentage: null,
          metric: "locally-sourced materials preferred"
        }
      },
      {
        id: "soil-health",
        title: "Soil Health",
        shortDescription: "Building healthy soil ecosystems that support long-term plant vitality",
        longDescription: "Organic soil amendments and composting create resilient landscapes that need fewer inputs",
        benefits: [
          "Improves plant health and longevity",
          "Increases soil water retention",
          "Reduces need for synthetic fertilizers"
        ],
        stats: {
          reductionPercentage: null,
          metric: "organic soil improvements"
        }
      }
    ],
  
    // Certifications and compliance
    certifications: [
      {
        id: "idaho-certified",
        title: "Idaho Certified Applicator",
        description: "Our technicians maintain proper certification and training for safe chemical application",
        authority: "Idaho State Department of Agriculture",
      },
      {
        id: "proper-notification",
        title: "Proper Notification Protocol",
        description: "All chemical applications are properly posted and documented according to regulations",
        authority: "State and Local Requirements",
      },
      {
        id: "watershed-compliance",
        title: "Watershed Compliance",
        description: "All practices meet or exceed local environmental protection standards",
        authority: "Sun Valley Environmental Standards",
      }
    ],
  
    // Environmental benefits
    benefits: [
      {
        category: "Safety",
        items: [
          "Safer for children and pets to play on treated areas",
          "Reduced exposure to harmful chemicals for families",
          "Non-toxic to beneficial insects and pollinators"
        ]
      },
      {
        category: "Environmental",
        items: [
          "Protects Sun Valley's pristine trout streams and wildlife",
          "Reduces environmental impact while maintaining beauty",
          "Supports local ecosystem health and biodiversity",
          "Contributes to cleaner air and water for our community"
        ]
      },
      {
        category: "Sustainability",
        items: [
          "Creates more resilient landscapes that adapt to climate changes",
          "Preserves natural resources for future generations",
          "Reduces dependence on synthetic chemicals and fertilizers"
        ]
      }
    ],
  
    // Statistics and achievements
    statistics: {
      yearsOfEcoFriendlyPractice: 33,
      waterReductionAverage: "40%",
      pesticideReductionAverage: "60%",
      nativePlantsWaterSavings: "50%",
      clientSatisfactionRate: "98%"
    },
  
    // Messaging for different contexts
    messaging: {
      residential: {
        headline: "Safe & Beautiful Landscapes for Your Family",
        focus: "Create stunning outdoor spaces that are safe for children and pets while protecting our environment"
      },
      commercial: {
        headline: "Environmentally Responsible Property Management",
        focus: "Maintain professional landscapes while demonstrating corporate environmental responsibility"
      },
      municipal: {
        headline: "Community-Safe Landscape Practices",
        focus: "Protect public health and local ecosystems while maintaining beautiful public spaces"
      }
    },
  
    // Call-to-action options
    callsToAction: [
      {
        type: "primary",
        text: "Request Eco-Friendly Consultation",
        link: "/EcoPage",
        description: "Learn how our sustainable practices can benefit your property"
      },
    ],
  
    // Testimonials related to eco-friendly practices
    testimonials: [
      {
        id: "eco-testimonial-1",
        text: "We love that All Seasons uses eco-friendly practices. Our kids can play safely on the lawn, and we know we're protecting Sun Valley's environment.",
        author: "Sarah Johnson",
        title: "Sun Valley Homeowner",
        location: "Sun Valley, ID"
      },
      {
        id: "eco-testimonial-2", 
        text: "Their sustainable approach aligns perfectly with our corporate environmental values. Beautiful results with minimal environmental impact.",
        author: "Michael Chen",
        title: "Facilities Manager",
        company: "Mountain Resort Properties"
      },
      {
        id: "eco-testimonial-3",
        text: "As pet owners, we appreciate All Seasons' commitment to pet-safe treatments. Our dogs can enjoy the yard worry-free.",
        author: "Lisa & Tom Martinez",
        title: "Homeowners",
        location: "Ketchum, ID"
      }
    ],
  
    // Educational content
    education: {
      tips: [
        {
          title: "Choose Native Plants",
          description: "Native plants are naturally adapted to our climate and require less water and maintenance.",
          difficulty: "Easy",
          impact: "High"
        },
        {
          title: "Install Drip Irrigation",
          description: "Drip systems deliver water directly to plant roots, reducing waste by up to 50%.",
          difficulty: "Moderate",
          impact: "High"
        },
        {
          title: "Compost Organic Matter",
          description: "Composting creates natural soil amendments that reduce need for chemical fertilizers.",
          difficulty: "Easy",
          impact: "Medium"
        },
        {
          title: "Create Wildlife Habitats",
          description: "Designated wild areas support beneficial insects and birds that naturally control pests.",
          difficulty: "Easy",
          impact: "Medium"
        }
      ],
      resources: [
        {
          title: "Idaho Native Plant Guide",
          type: "PDF",
          description: "Comprehensive guide to plants native to the Sun Valley region"
        },
        {
          title: "Water-Wise Landscaping Tips",
          type: "Article",
          description: "Practical advice for reducing landscape water consumption"
        },
        {
          title: "IPM Best Practices",
          type: "Guide",
          description: "Understanding integrated pest management principles"
        }
      ]
    },
  
    // Seasonal considerations
    seasonal: {
      spring: {
        focus: "Soil preparation and native plant installation",
        practices: ["Organic soil amendments", "Native seeding", "Natural fertilizers"]
      },
      summer: {
        focus: "Water conservation and minimal intervention pest management", 
        practices: ["Efficient irrigation", "Beneficial insect habitat", "Drought-tolerant maintenance"]
      },
      fall: {
        focus: "Natural preparation for winter and habitat preservation",
        practices: ["Organic mulching", "Wildlife habitat preservation", "Natural winterization"]
      },
      winter: {
        focus: "Minimal intervention and planning for sustainable growth",
        practices: ["Planning sustainable improvements", "Equipment maintenance", "Educational preparation"]
      }
    }
  };
  
  // Helper functions for data access
  export const getEcoFriendlyPractices = () => ecoFriendlyData.practices;
  
  export const getEcoFriendlyPracticeById = (id) => 
    ecoFriendlyData.practices.find(practice => practice.id === id);
  
  export const getEcoFriendlyBenefits = () => 
    ecoFriendlyData.benefits.reduce((all, category) => [...all, ...category.items], []);
  
  export const getEcoFriendlyMessaging = (context = 'residential') => 
    ecoFriendlyData.messaging[context] || ecoFriendlyData.messaging.residential;
  
  export const getSeasonalPractices = (season) => 
    ecoFriendlyData.seasonal[season] || ecoFriendlyData.seasonal.spring;
  
  export const getRandomEcoTip = () => {
    const tips = ecoFriendlyData.education.tips;
    return tips[Math.floor(Math.random() * tips.length)];
  };
  
  export const getEcoTestimonials = () => ecoFriendlyData.testimonials;
  
  // Export default for easy importing
  export default ecoFriendlyData;